// URL state management

export type WritingStage = "not-started" | "stuck" | "draft-review";

export interface AppState {
  writingStage?: WritingStage;
  topics: string[];
  audience: {
    closeness?: "close" | "extended";
    agreement?: "sympathetic" | "some-disagree";
    neighbors?: boolean;
    faith?: boolean;
  };
  blockers: string[];
}

export function parseState(): AppState {
  const params = new URLSearchParams(window.location.search);

  const writingStage = params.get("writing-stage") as WritingStage | null;
  const topics = params.get("topics")?.split(",").filter(Boolean) || [];
  const closeness = params.get("closeness") as AppState["audience"]["closeness"];
  const agreement = params.get("agreement") as AppState["audience"]["agreement"];
  const neighbors = params.get("neighbors") === "true";
  const faith = params.get("faith") === "true";
  const blockers = params.get("blockers")?.split(",").filter(Boolean) || [];

  return {
    writingStage: writingStage || undefined,
    topics,
    audience: {
      closeness,
      agreement,
      neighbors: neighbors || undefined,
      faith: faith || undefined,
    },
    blockers,
  };
}

export function serializeState(state: AppState): string {
  const params = new URLSearchParams();

  if (state.writingStage) {
    params.set("writing-stage", state.writingStage);
  }
  if (state.topics.length > 0) {
    params.set("topics", state.topics.join(","));
  }
  if (state.audience.closeness) {
    params.set("closeness", state.audience.closeness);
  }
  if (state.audience.agreement) {
    params.set("agreement", state.audience.agreement);
  }
  if (state.audience.neighbors) {
    params.set("neighbors", "true");
  }
  if (state.audience.faith) {
    params.set("faith", "true");
  }
  if (state.blockers.length > 0) {
    params.set("blockers", state.blockers.join(","));
  }

  const str = params.toString();
  return str ? `?${str}` : "";
}

export function updateUrl(state: AppState): void {
  const query = serializeState(state);
  const newUrl = window.location.pathname + query;
  window.history.replaceState(null, "", newUrl);
}

export function getStateForLink(state: AppState, path: string): string {
  return path + serializeState(state);
}

// localStorage key for read items
const READ_STORAGE_KEY = "fywmn-read";

export function getReadItems(): Set<string> {
  try {
    const data = localStorage.getItem(READ_STORAGE_KEY);
    if (data) {
      return new Set(JSON.parse(data));
    }
  } catch {
    // Ignore parse errors
  }
  return new Set();
}

export function markAsRead(itemId: string): void {
  const read = getReadItems();
  read.add(itemId);
  localStorage.setItem(READ_STORAGE_KEY, JSON.stringify([...read]));
}

export function unmarkAsRead(itemId: string): void {
  const read = getReadItems();
  read.delete(itemId);
  localStorage.setItem(READ_STORAGE_KEY, JSON.stringify([...read]));
}

export function isRead(itemId: string): boolean {
  return getReadItems().has(itemId);
}

// localStorage key for starred items
const STARRED_STORAGE_KEY = "fywmn-starred";

export function getStarredItems(): Set<string> {
  try {
    const data = localStorage.getItem(STARRED_STORAGE_KEY);
    if (data) {
      return new Set(JSON.parse(data));
    }
  } catch {
    // Ignore parse errors
  }
  return new Set();
}

export function toggleStarred(itemId: string): boolean {
  const starred = getStarredItems();
  const isNowStarred = !starred.has(itemId);
  if (isNowStarred) {
    starred.add(itemId);
  } else {
    starred.delete(itemId);
  }
  localStorage.setItem(STARRED_STORAGE_KEY, JSON.stringify([...starred]));
  return isNowStarred;
}

export function isStarred(itemId: string): boolean {
  return getStarredItems().has(itemId);
}
